import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { ReferralAdminItem } from "../../../types/referral";

type StatusChipColor = "warning" | "success" | "default" | "error";

const statusColor = (status: string): StatusChipColor => {
  switch (status) {
    case "Pending": return "warning";
    case "Completed": return "success";
    case "Expired": return "default";
    case "Cancelled": return "error";
    default: return "default";
  }
};

interface ReferralTableProps {
  referrals: ReferralAdminItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onViewDetail?: (id: string) => void;
  loading?: boolean;
}

const ReferralTable: React.FC<ReferralTableProps> = ({
  referrals,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onViewDetail,
  loading,
}) => {
  const { t } = useTranslation();

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("admin.referral.referrer")}</TableCell>
              <TableCell>{t("admin.referral.referee")}</TableCell>
              <TableCell>{t("admin.referral.status")}</TableCell>
              <TableCell>{t("admin.referral.date")}</TableCell>
              <TableCell>{t("admin.referral.completedDate")}</TableCell>
              <TableCell>{t("admin.referral.orderNumber")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : referrals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    {t("admin.referral.noReferralsFound")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              referrals.map((item) => (
                <TableRow
                  key={item.id}
                  hover
                  sx={{ cursor: onViewDetail ? "pointer" : "default" }}
                  onClick={() => onViewDetail?.(item.id)}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {item.referrerName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.referrerEmail}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {item.refereeName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.refereeEmail}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t(`admin.referral.statusLabel.${item.status.toLowerCase()}`)}
                      color={statusColor(item.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(item.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {item.completedAt
                      ? new Date(item.completedAt).toLocaleDateString()
                      : "—"}
                  </TableCell>
                  <TableCell>{item.orderNumber ?? "—"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[pageSize]}
        onPageChange={(_e, newPage) => onPageChange(newPage)}
      />
    </Paper>
  );
};

export default ReferralTable;
